import { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import DialogContext from './DialogContext';
import Dialog from 'Components/Molecules/Dialog/Dialog';
import { useErrors } from 'Components/Organisms/ErrorsProvider';

const dialogProps = (dialog) => {
    if (!dialog) return {};

    return {
        ...dialog.component.dialogProps,
        ...dialog.props.dialogProps,
    };
};

export default function DialogProvider({ children, sharedDialogs = [] }) {
    const [currentDialog, setCurrentDialog] = useState(null);
    const [dialogTypes, setDialogTypes] = useState(() => {
        return sharedDialogs.reduce((acc, [id, component]) => {
            acc[id] = component;
            return acc;
        }, {});
    });

    const { showUnknownError } = useErrors();

    const register = useCallback((paramsOrId, component) => {
        function registerSingleDialog(id, Component) {
            setDialogTypes((prev) => ({ ...prev, [id]: Component }));

            return () => {
                setDialogTypes((prev) => {
                    const { [id]: _, ...rest } = prev;
                    return rest;
                });
            };
        }

        function registerMultipleDialog(dialogObject) {
            setDialogTypes((prev) => ({ ...prev, ...dialogObject }));

            return () => {
                setDialogTypes((prev) => {
                    const newTypes = { ...prev };
                    Object.keys(dialogObject).forEach((id) => {
                        delete newTypes[id];
                    });
                    return newTypes;
                });
            };
        }

        if (Array.isArray(paramsOrId)) {
            const dialogObject = paramsOrId.reduce((acc, [id, component]) => {
                acc[id] = component;
                return acc;
            }, {});
            return registerMultipleDialog(dialogObject);
        } else {
            return registerSingleDialog(paramsOrId, component);
        }
    }, []);

    const open = useCallback(
        (id, props = {}) => {
            if (!dialogTypes[id]) {
                console.warn(`Dialog type "${id}" is not registered.`);
                return;
            }
            const component = dialogTypes[id];
            setCurrentDialog({ id, component, props });
        },
        [dialogTypes],
    );

    const contextValue = useMemo(
        () => ({
            register,
            open,
        }),
        [register, open],
    );

    const hasCurrentDialog = Boolean(currentDialog);
    const currentDialogProps = dialogProps(currentDialog);

    const handleCloseDialog = () => {
        setCurrentDialog(null);
    };

    const handleClose = () => {
        if (currentDialog && currentDialog.props.onClose) {
            currentDialog.props.onClose();
        }
        handleCloseDialog();
    };

    const handleError = (error) => {
        showUnknownError(error);
        handleCloseDialog();
    };

    const handleSubmit = async (data) => {
        if (currentDialog && currentDialog.props.onSubmit) {
            await currentDialog.props.onSubmit(data);
        }
        handleCloseDialog();
    };

    return (
        <DialogContext.Provider value={contextValue}>
            {children}

            <Dialog open={hasCurrentDialog} onClose={handleClose} size="md" {...currentDialogProps}>
                {hasCurrentDialog && (
                    <currentDialog.component
                        {...currentDialog.props}
                        onClose={handleClose}
                        onSubmit={handleSubmit}
                        onError={handleError}
                    />
                )}
            </Dialog>
        </DialogContext.Provider>
    );
}

DialogProvider.propTypes = {
    children: PropTypes.node.isRequired,
    sharedDialogs: PropTypes.arrayOf(PropTypes.array),
};
